import { combineLatest, Observable, of } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { PaginatedResponse } from 'src/app/shared/interfaces'
import { buildHttpParams } from 'src/app/shared/query-utils'
import { ResourceService } from 'src/app/shared/resource-service'
import { environment } from 'src/environments/environment'
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { PurchaseParams, SaleItem } from './sale-item'
import { SaleItemSerializer } from './sale-item.serializer'
import { SafeHtml } from '@angular/platform-browser'
import { TranslateService } from '@ngx-translate/core'
import moment from 'moment-mini'
import { AppState } from '../../../core/core.state'
import { Store } from '@ngrx/store'
import { productSelectors } from '../../product/product.selectors'
import { subscriptionSelectors } from '../../subscription/subscription.selectors'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { courseSelectors } from '../../course/course.selectors'
import { subscriberSelectors } from '../../subscription/subscriber/subscriber.selectors'
import { SubscriberService } from '../../subscription/subscriber/subscriber.service'
import { SubscriptionService } from '../../subscription/subscription.service'
import { Subscription } from '../../subscription/subscription'
import { bookingSelectors } from '../../booking/booking.selectors'
import { RoundMinutesPipe } from 'src/app/shared/pipes/round-minutes.pipe'
import { getPriceForCurrentMonth } from 'src/app/shared/utils'
import { SaleService } from '../sale.service'
import { FormatDatePipe } from '../../../shared/pipes/format-date.pipe'
import { ProductService } from '../../product/product.service'
import { CourseService } from '../../course/course.service'
import { FamilyMemberService } from '../../family/family-members/family-member.service'
import { FamilyMember } from '../../family/family-members/family-member'
import { Member } from '../../member/member'
import { Subscriber } from '../../subscription/subscriber/subscriber'

export interface SaleItemFamilyPriceDetail {
  memberId: number
  name: string
  price: number
  qty: number
  categoryId: number | null
}

export interface SaleItemPriceDetails {
  price?: number
  guestPrice?: number
  otherPrice?: number
  memberPrice?: number
  qty?: number
  guestQty?: number
  otherQty?: number
  memberQty?: number
  total?: number
  categoryGuestPrice?: number
  category?: number
  categoryQty?: number
  familyPriceDetails?: SaleItemFamilyPriceDetail[]
}

const emptyPriceDetails = {
  price: 0,
  guestPrice: 0,
  otherPrice: 0,
  memberPrice: 0,
  qty: 0,
  guestQty: 0,
  otherQty: 0,
  memberQty: 0,
  total: 0,
  categoryGuestPrice: 0,
  category: null,
  categoryQty: 0,
  familyPriceDetails: []
}

@Injectable()
export class SaleItemService extends ResourceService<SaleItem> {
  // TODO create some resource service to extend that works for services like this
  constructor(
    public http: HttpClient,
    public serializer: SaleItemSerializer,
    private translate: TranslateService,
    private store: Store<AppState>,
    private subscriberService: SubscriberService,
    private saleService: SaleService,
    private subscriptionService: SubscriptionService,
    private roundMinutes: RoundMinutesPipe,
    private dateFormatPipe: FormatDatePipe,
    private productService: ProductService,
    private courseService: CourseService,
    private familyMemberService: FamilyMemberService
  ) {
    super(http, `${environment.backendApiUrl}/sale-items`, serializer)
  }

  public getSaleItemDetail(saleItem: SaleItem): Observable<SafeHtml> {
    const translations$ = combineLatest([
      this.translate.get('cartModule.invitedGuestAmount'),
      this.translate.get('cartModule.invitedMemberEmails'),
      this.translate.get('bookingModule.time'),
      this.translate.get('cartModule.totalParticipants')
    ])

    const saleItemPriceDetails$ = saleItem.subscriptionId
      ? this.getSaleItemSubscriptionPriceDetails$(saleItem)
      : this.getSaleItemProductPriceDetails$(saleItem)

    const storeData$ = combineLatest([
      this.store.select(productSelectors.selectEntities),
      saleItemPriceDetails$
    ])

    return combineLatest([translations$, storeData$]).pipe(
      take(1),
      map(
        ([
          [
            invitedGuestsTranslation,
            invitedMembersTranslation,
            timeTranslation,
            totalParticipantsTranslation
          ],
          [productEntities, saleItemProductPriceDetail]
        ]) => {
          let details = ''
          //Put the data-test-id='sale-invited-members' inside class, because was not working putting on the html tag
          if (saleItem?.params?.invitedMemberEmails?.length) {
            details += `
            <p class='m-0 p-0 sale-invited-members'><small>${invitedMembersTranslation}:
                ${saleItem.params.invitedMemberEmails.join(', ')}
            </small></p>
            `
          }

          if (saleItemProductPriceDetail.familyPriceDetails?.length > 0) {
            const familyMemberDetails =
              saleItemProductPriceDetail.familyPriceDetails

            if (familyMemberDetails.length > 0) {
              details += `
              <p class='m-0 p-0 sale-invited-family-members' ><small>${this.translate.instant('cartModule.invitedFamilyMembers')}:
                  ${familyMemberDetails.map((detail) => `<p  class='m-1 invited-family-member'>${detail.name}: €${detail.price.toFixed(2)}</p>`).join('')}
              </small></p>
              `
            }
          }
          if (saleItem && saleItem.params) {
            if (
              !saleItem.subscriptionId &&
              (saleItem.params.invitedGuests?.length ||
                saleItem.params.invitedOthers?.length ||
                saleItem.params.familyMembersInvited?.length)
            ) {
              details += `
                <p class='m-0 p-0'><small>${totalParticipantsTranslation}:
                    ${this.getSaleItemParticipantCount(
                      saleItem,
                      productEntities
                    )}
                </small></p>
                `
            }
          }

          if (
            saleItem.params?.bookerAssisting &&
            saleItem.params?.familyMembersInvited?.length
          ) {
            details += `<small class="m-0 "> ${this.translate.instant('cartModule.bookerIsFamily')}</small> <br>`
          }

          if (saleItem.productId && saleItem.params?.startDate) {
            const startDate = moment(saleItem.params.startDate)
            const endDate = moment(saleItem.params.endDate)
            const isOneDayBooking = endDate.isSame(startDate, 'day')
            combineLatest(
              this.dateFormatPipe.transform(startDate, 'EE d MMM y'),
              this.dateFormatPipe.transform(endDate, 'EE d MMM y')
            )
              .pipe(take(1))
              .subscribe(([startDate, endDate]) => {
                details += `
                <small class='m-0 p-0'> ${timeTranslation}:

                    ${moment(saleItem.params.startDate).format('HH:mm')} ${
                      !isOneDayBooking ? startDate : ''
                    } - ${moment(
                      moment(saleItem.params.endDate)
                        .toDate()
                        .setMinutes(
                          this.roundMinutes.transform(
                            moment(saleItem.params.endDate)
                              .toDate()
                              .getMinutes()
                          )
                        )
                    ).format('HH:mm')} ${endDate}
                </small>`
              })
          }
          return details
        }
      )
    )
  }

  private getSaleItemParticipantCount(
    saleItem: SaleItem,
    productEntities
  ): number {
    const familyMembersInvitedCount =
      saleItem.params?.familyMembersInvited?.length ?? 0
    let totalParticipants = saleItem.params?.invitedGuests?.length ?? 0
    totalParticipants += saleItem.params?.invitedOthers?.length ?? 0
    totalParticipants += saleItem.params?.invitedMemberEmails?.length ?? 0
    totalParticipants += familyMembersInvitedCount

    const targetProduct =
      productEntities?.[
        saleItem.params?.bookableLinkedProductId
          ? saleItem.params?.bookableLinkedProductId
          : saleItem.productId
      ]

    const shouldCountBookerAsParticipant =
      familyMembersInvitedCount > 0
        ? (saleItem.params?.bookerAssisting ?? true)
        : targetProduct?.bookerMustBeParticipant

    if (shouldCountBookerAsParticipant) {
      totalParticipants += 1
    }

    return totalParticipants
  }

  public getManyBySaleId(
    saleId: number,
    params?: RequestQueryBuilder
  ): Observable<SaleItem[]> {
    return this.http
      .get<PaginatedResponse>(
        `${environment.backendApiUrl}/sales/${saleId}/sale-items`,
        {
          params: buildHttpParams(params)
        }
      )
      .pipe(
        map((reponse) =>
          reponse.data.map(
            (data: any) => this.serializer.fromJson(data) as SaleItem
          )
        )
      )
  }

  public getOneBySaleId(
    saleId: number,
    saleLine: number,
    params?: RequestQueryBuilder
  ): Observable<SaleItem> {
    return this.http.get<SaleItem>(
      `${environment.backendApiUrl}/sales/${saleId}/sale-items/${saleLine}`,
      {
        params: buildHttpParams(params)
      }
    )
  }

  public updateOneBySaleId(
    saleId: number,
    saleLine: number,
    params: Partial<SaleItem>
  ): Observable<SaleItem> {
    return this.http.patch<SaleItem>(
      `${environment.backendApiUrl}/sales/${saleId}/sale-items/${saleLine}`,
      {
        ...params
      }
    )
  }

  public deleteOneBySaleId(
    saleId: number,
    saleLine: number
  ): Observable<boolean> {
    return this.http.delete<any>(
      `${environment.backendApiUrl}/sales/${saleId}/sale-items/${saleLine}`
    )
  }

  public getSaleItemProductPriceDetails$(
    saleItem: SaleItem
  ): Observable<SaleItemPriceDetails> {
    const familyMembers$ = saleItem.params?.familyMembersInvited?.length
      ? this.familyMemberService.getAll()
      : of([])

    return combineLatest([
      this.store.select(productSelectors.selectEntities),
      this.store.select(subscriberSelectors.selectAll),
      this.store.select(authSelectors.selectMember),
      this.saleService.isTicketSale$(null, saleItem?.saleId),
      familyMembers$
    ]).pipe(
      map(
        ([
          productEntities,
          subscribers,
          member,
          isTicketSale,
          familyMembers
        ]) => {
          const product = productEntities[saleItem.productId]
          const fallthroughProduct =
            productEntities[saleItem.params?.bookableProductId]

          if (!product) {
            return {
              price: 0,
              qty: 0,
              total: 0
            }
          }

          const targetCategory = Number(saleItem.customerCategory)
          const params = saleItem.params
          const result = { ...emptyPriceDetails }
          const shouldIncludeBookerPrice = !(
            params?.familyMembersInvited?.length &&
            params?.bookerAssisting === false
          )
          const familyPriceDetails = this.getFamilyPriceDetails(
            params?.familyMembersInvited,
            familyMembers,
            subscribers,
            (familyMember, memberSubscribers) =>
              this.productService.getPriceForMember(
                product,
                familyMember,
                memberSubscribers,
                productEntities,
                {
                  ...params,
                  invitedGuests: [],
                  invitedOthers: [],
                  invitedMemberEmails: []
                }
              )
          )

          const invitedGuestsWithCategory = params?.invitedGuests?.filter(
            (invitedGuest) =>
              invitedGuest?.category !== null &&
              invitedGuest?.category !== undefined
          )

          if (invitedGuestsWithCategory?.length) {
            const guestCategory = invitedGuestsWithCategory[0]?.category

            let categoryPrice: number
            if (guestCategory == 0) {
              categoryPrice =
                product?.price !== null
                  ? product.price
                  : fallthroughProduct?.price
            }
            if (guestCategory > 0) {
              categoryPrice =
                product[`category${guestCategory}Price`] !== null
                  ? product[`category${guestCategory}Price`]
                  : fallthroughProduct?.[`category${guestCategory}Price`]
            }

            result.memberPrice = 0
            result.category = guestCategory
            result.categoryGuestPrice = categoryPrice
            result.categoryQty = invitedGuestsWithCategory.length

            return this.withFamilyPriceDetails(result, familyPriceDetails)
          }

          const invitedGuestsWithoutCategory = params?.invitedGuests?.filter(
            (invitedGuest) => !invitedGuest?.category
          )

          const invitedGuests = invitedGuestsWithoutCategory?.length ?? 0
          const invitedOthers = params?.invitedOthers?.length ?? 0

          if (
            invitedGuests &&
            product.allowChooseCategory &&
            product.paymentPerParticipant
          ) {
            return this.withFamilyPriceDetails(
              {
                ...result,
                guestPrice:
                  targetCategory == 0
                    ? product['price']
                    : product[`category${targetCategory}Price`],
                guestQty: invitedGuests,
                total:
                  invitedGuests * targetCategory == 0
                    ? product['price']
                    : product[`category${targetCategory}Price`]
              },
              familyPriceDetails
            )
          }

          if (
            invitedGuests &&
            (!product.allowChooseCategory || product.paymentPerParticipant)
          ) {
            result.guestQty = invitedGuests
            result.guestPrice =
              product.guestPrice !== null
                ? product.guestPrice
                : fallthroughProduct?.guestPrice
            result.total += invitedGuests * result.guestPrice
          }

          if (invitedOthers) {
            result.otherQty += invitedOthers
            result.otherPrice =
              product.otherInvitePrice !== null
                ? product.otherInvitePrice
                : fallthroughProduct?.otherInvitePrice
            result.total += invitedOthers * result.otherPrice
          }

          if (
            shouldIncludeBookerPrice &&
            product.linkedSubsPrice !== null &&
            product.linkedSubscriptions?.length &&
            params?.startDate
          ) {
            if (
              this.subscriberService.hasActiveSubscription(
                subscribers.filter((s) => s.memberId == member.id),
                product.linkedSubscriptions,
                moment.isMoment(params.startDate)
                  ? params.startDate
                  : moment(params.startDate)
              )
            ) {
              result.total += parseFloat(String(product.linkedSubsPrice))
              result.price = parseFloat(String(product.linkedSubsPrice))
              result.qty = saleItem.quantity ? saleItem.quantity : 1
              return this.withFamilyPriceDetails(result, familyPriceDetails)
            }
          }

          const resolvedPrice =
            product.price === null && fallthroughProduct
              ? fallthroughProduct.price
              : product.price

          if (targetCategory === null || targetCategory == 0) {
            if (shouldIncludeBookerPrice) {
              result.total += Number(resolvedPrice)
              result.price = Number(resolvedPrice)
              result.qty = saleItem.quantity ? saleItem.quantity : 1
            }
            return this.withFamilyPriceDetails(result, familyPriceDetails)
          }

          if (![1, 2, 3, 4, 5, 6, 7, 8, 9].includes(Number(targetCategory))) {
            console.error('Invalid category')
            return this.withFamilyPriceDetails(
              { ...emptyPriceDetails },
              familyPriceDetails
            )
          }

          let priceForMember

          if (isTicketSale && saleItem.productId) {
            priceForMember =
              product[`category${targetCategory}Price`] === null &&
              fallthroughProduct
                ? fallthroughProduct[`category${targetCategory}Price`]
                : product[`category${targetCategory}Price`]
          } else {
            priceForMember =
              product[`category${targetCategory}Price`] === null
                ? product.price
                : product[`category${targetCategory}Price`]

            if (priceForMember == null || priceForMember == undefined) {
              priceForMember =
                fallthroughProduct &&
                fallthroughProduct[`category${targetCategory}Price`]
                  ? fallthroughProduct[`category${targetCategory}Price`]
                  : fallthroughProduct?.price
            }
          }

          if (priceForMember === null) {
            if (shouldIncludeBookerPrice) {
              result.total += Number(resolvedPrice)
              result.price = Number(resolvedPrice)
              result.qty = saleItem.quantity ? saleItem.quantity : 1
            }
            return this.withFamilyPriceDetails(result, familyPriceDetails)
          }

          if (shouldIncludeBookerPrice) {
            result.total += priceForMember
            result.memberPrice = priceForMember
            result.memberQty = saleItem.quantity ? saleItem.quantity : 1
            result.qty = saleItem.quantity ? saleItem.quantity : 1
          }

          return this.withFamilyPriceDetails(result, familyPriceDetails)
        }
      )
    )
  }

  public getSaleItemSubscriptionPriceDetails$(
    saleItem: SaleItem
  ): Observable<SaleItemPriceDetails> {
    const familyMembers$ = saleItem.params?.familyMembersInvited?.length
      ? this.familyMemberService.getAll()
      : of([])

    return combineLatest([
      this.store.select(subscriptionSelectors.selectEntities),
      this.store.select(authSelectors.selectMember),
      familyMembers$
    ]).pipe(
      map(([subscriptionEntities, member, familyMembers]) => {
        const subscription = subscriptionEntities[saleItem.subscriptionId]

        if (!subscription) {
          return {
            price: 0,
            qty: 0,
            total: 0
          }
        }

        const params = saleItem.params
        const result = { ...emptyPriceDetails }
        const familyPriceDetails = this.getFamilyPriceDetails(
          params?.familyMembersInvited,
          familyMembers,
          [],
          (familyMember) =>
            this.getSubscriptionPriceForMember(
              subscription,
              familyMember,
              params
            )
        )

        if (familyPriceDetails.length) {
          result.total += familyPriceDetails.reduce(
            (total, familyPriceDetail) => total + familyPriceDetail.price,
            0
          )

          return this.withFamilyPriceDetails(result, familyPriceDetails)
        }

        const priceForMember = this.getSubscriptionPriceForMember(
          subscription,
          member,
          params
        )

        result.total += priceForMember
        result.price = priceForMember
        result.qty = saleItem.quantity ? saleItem.quantity : 1

        return this.withFamilyPriceDetails(result, familyPriceDetails)
      })
    )
  }

  private getSubscriptionPriceForMember(
    subscription: Subscription,
    member: Member,
    params?: PurchaseParams
  ): number {
    if (!subscription) {
      return 0
    }

    const startDate = this.subscriptionService.getStartDate(
      subscription?.defaultStartDate ??
        params?.defaultStartDate ??
        params?.startDate
    )
    const priceParams = {
      ...params,
      startDate: moment(startDate)
    }
    const priceForMember = this.subscriptionService.getPriceForMember(
      subscription,
      member,
      false,
      priceParams,
      this.subscriptionService.lastDebitCharge(subscription, moment(startDate))
    )

    return subscription?.isFixedTerms
      ? getPriceForCurrentMonth(
          priceForMember,
          moment(startDate).format('YYYY-MM-DD')
        )
      : priceForMember
  }

  public getSaleItemCoursePrice$(
    saleItem: SaleItem
  ): Observable<SaleItemPriceDetails> {
    const familyMembers$ = saleItem.params?.familyMembersInvited?.length
      ? this.familyMemberService.getAll()
      : of([])

    return combineLatest([
      this.store.select(courseSelectors.selectEntities),
      this.store.select(productSelectors.selectEntities),
      this.store.select(subscriberSelectors.selectAll),
      this.store.select(authSelectors.selectMember),
      this.store.select(bookingSelectors.selectEntities),
      familyMembers$
    ]).pipe(
      map(
        ([
          courseEntities,
          productEntities,
          subscribers,
          member,
          bookingEntities,
          familyMembers
        ]) => {
          const course = courseEntities[saleItem.courseId]
          if (!course) {
            return {
              price: 0,
              qty: 0,
              total: 0
            }
          }

          const targetCategory = Number(
            saleItem.customerCategory ?? member.category
          )

          const result = { ...emptyPriceDetails }
          const shouldIncludeBookerPrice = !(
            saleItem.params?.familyMembersInvited?.length &&
            saleItem.params?.bookerAssisting === false
          )

          const courseProduct = productEntities[course.productId]
          const familyPriceDetails = this.getFamilyPriceDetails(
            saleItem.params?.familyMembersInvited,
            familyMembers,
            subscribers,
            (familyMember, memberSubscribers) =>
              this.courseService.getPriceForMember(
                course,
                courseProduct,
                familyMember,
                memberSubscribers,
                bookingEntities
              )
          )

          const courseBookings = course.bookings
            ?.map((bookingId) => bookingEntities[bookingId])
            .sort((a, b) => {
              return a?.startDate.valueOf() < b?.startDate.valueOf() ? -1 : 1
            })

          const startDate =
            courseBookings?.[0]?.startDate &&
            moment(courseBookings?.[0]?.startDate)?.isAfter(moment())
              ? courseBookings?.[0]?.startDate
              : moment()

          const endDate =
            courseBookings?.[courseBookings.length - 1]?.startDate ?? moment()

          if (
            shouldIncludeBookerPrice &&
            course.linkedSubsPrice !== null &&
            courseProduct?.linkedSubscriptions?.length
          ) {
            if (
              this.subscriberService.hasActiveSubscription(
                subscribers.filter((s) => s.memberId == member.id),
                courseProduct?.linkedSubscriptions,
                startDate
              ) &&
              this.subscriberService.hasActiveSubscription(
                subscribers.filter((s) => s.memberId == member.id),
                courseProduct?.linkedSubscriptions,
                endDate
              )
            ) {
              result.total += parseFloat(String(course.linkedSubsPrice))
              result.price = parseFloat(String(course.linkedSubsPrice))
              result.qty = saleItem.quantity ? saleItem.quantity : 1

              return this.withFamilyPriceDetails(result, familyPriceDetails)
            }
          }

          if (targetCategory === null || targetCategory == 0) {
            if (shouldIncludeBookerPrice) {
              result.total += Number(course.price)
              result.price = Number(course.price)
              result.qty = 1
            }
            return this.withFamilyPriceDetails(result, familyPriceDetails)
          }

          if (![1, 2, 3, 4, 5, 6, 7, 8, 9].includes(Number(targetCategory))) {
            console.error('Invalid category')
            return this.withFamilyPriceDetails(
              { ...emptyPriceDetails },
              familyPriceDetails
            )
          }

          const priceForMember: number =
            course[`category${targetCategory}Price`]

          if (priceForMember === null) {
            if (shouldIncludeBookerPrice) {
              result.total += Number(course.price)
              result.price = Number(course.price)
              result.qty = 1
            }
            return this.withFamilyPriceDetails(result, familyPriceDetails)
          }
          if (shouldIncludeBookerPrice) {
            result.total += priceForMember
            result.memberPrice = priceForMember
            result.memberQty = 1
          }
          result.otherQty = saleItem.params?.invitedOthers?.length
          result.otherPrice = saleItem.params?.invitedOthers?.length
            ? productEntities[course.productId].otherInvitePrice
            : null
          result.guestQty = saleItem.params?.invitedGuests?.length
          result.guestPrice = saleItem.params?.invitedGuests?.length
            ? productEntities[course.productId].guestPrice
            : null

          return this.withFamilyPriceDetails(result, familyPriceDetails)
        }
      )
    )
  }

  private getFamilyPriceDetails(
    familyMemberIds: number[] = [],
    familyMembers: FamilyMember[] = [],
    subscribers: Subscriber[] = [],
    getPrice: (familyMember: Member, memberSubscribers: Subscriber[]) => number
  ): SaleItemFamilyPriceDetail[] {
    if (!familyMemberIds?.length) {
      return []
    }

    return familyMemberIds
      .map((familyMemberId) => {
        const familyMember = familyMembers.find(
          (fm) => fm.memberId === Number(familyMemberId)
        )?.member

        if (!familyMember) {
          return null
        }

        const memberSubscribers = subscribers.filter(
          (subscriber) => subscriber.memberId === familyMember.id
        )
        const firstName = familyMember.firstName?.trim() ?? ''
        const lastName = familyMember.lastName?.trim() ?? ''

        return {
          memberId: Number(familyMemberId),
          name: [firstName, lastName].filter(Boolean).join(' '),
          price: getPrice(familyMember, memberSubscribers),
          qty: 1,
          categoryId:
            familyMember.category !== null &&
            familyMember.category !== undefined
              ? Number(familyMember.category)
              : null
        }
      })
      .filter(
        (familyPriceDetail): familyPriceDetail is SaleItemFamilyPriceDetail =>
          familyPriceDetail !== null
      )
  }

  private withFamilyPriceDetails(
    priceDetails: SaleItemPriceDetails,
    familyPriceDetails: SaleItemFamilyPriceDetail[]
  ): SaleItemPriceDetails {
    return {
      ...priceDetails,
      familyPriceDetails
    }
  }
}
